/**
 * VariantCombinations.ts - Combine multiple variant types
 * 
 * CHANGE HISTORY:
 * - 251102_1715 danielbeer: Created for Phase 3.5b - Advanced Features
 * 
 * Automatically generate combinations of size + color + state variants
 */

import type { Style, StyleVariables, CreateStyleParams } from '../types';
import { VARIANT_TEMPLATES } from './VariantManager';

export interface CombinationConfig {
  sizes?: boolean;
  colors?: boolean;
  states?: boolean;
  namePattern?: string;
  gallery?: string;
}

export class VariantCombinationManager {
  private createStyleFn: (params: CreateStyleParams) => Style;

  constructor(createStyleFn: (params: CreateStyleParams) => Style) {
    this.createStyleFn = createStyleFn;
  }

  /**
   * Create all combinations of variants
   */
  createCombinations(
    baseStyle: Style,
    config: CombinationConfig = {}
  ): Style[] {
    const variants: Style[] = [];
    const {
      sizes = true,
      colors = true,
      states = false, // States usually applied separately
      namePattern = '{base} - {size} {color}',
      gallery = 'combinations'
    } = config;

    const sizeKeys = sizes ? Object.keys(VARIANT_TEMPLATES.sizes) : [''];
    const colorKeys = colors ? Object.keys(VARIANT_TEMPLATES.colors) : [''];
    const stateKeys = states ? Object.keys(VARIANT_TEMPLATES.states) : [''];

    for (const sizeKey of sizeKeys) {
      for (const colorKey of colorKeys) {
        for (const stateKey of stateKeys) {
          // Skip if no variants selected
          if (!sizeKey && !colorKey && !stateKey) continue;

          const variables: Partial<StyleVariables> = {};
          const nameParts: string[] = [];

          if (sizeKey) {
            const sizeTemplate = VARIANT_TEMPLATES.sizes[sizeKey as keyof typeof VARIANT_TEMPLATES.sizes];
            Object.assign(variables, sizeTemplate.variables);
            nameParts.push(sizeTemplate.name);
          }

          if (colorKey) {
            const colorTemplate = VARIANT_TEMPLATES.colors[colorKey as keyof typeof VARIANT_TEMPLATES.colors];
            Object.assign(variables, colorTemplate.variables);
            nameParts.push(colorTemplate.name);
          }

          if (stateKey) {
            const stateTemplate = VARIANT_TEMPLATES.states[stateKey as keyof typeof VARIANT_TEMPLATES.states];
            Object.assign(variables, stateTemplate.variables);
            nameParts.push(stateTemplate.name);
          }

          const name = namePattern
            .replace('{base}', baseStyle.name)
            .replace('{size}', sizeKey ? VARIANT_TEMPLATES.sizes[sizeKey as keyof typeof VARIANT_TEMPLATES.sizes].name : '')
            .replace('{color}', colorKey ? VARIANT_TEMPLATES.colors[colorKey as keyof typeof VARIANT_TEMPLATES.colors].name : '')
            .replace('{state}', stateKey ? VARIANT_TEMPLATES.states[stateKey as keyof typeof VARIANT_TEMPLATES.states].name : '')
            .replace(/\s+/g, ' ')
            .trim();

          const variant = this.createStyleFn({
            name,
            type: baseStyle.type,
            extends: baseStyle.id,
            variables: variables as StyleVariables,
            gallery,
            metadata: {
              baseStyle: baseStyle.id,
              isVariant: true,
              isCombination: true,
              size: sizeKey || undefined,
              color: colorKey || undefined,
              state: stateKey || undefined
            }
          });

          variants.push(variant);
        }
      }
    }

    return variants;
  }

  /**
   * Create size + color combinations (most common)
   */
  createSizeColorCombinations(baseStyle: Style, gallery?: string): Style[] {
    return this.createCombinations(baseStyle, {
      sizes: true,
      colors: true,
      states: false,
      gallery
    });
  }
}

let combinationManagerInstance: VariantCombinationManager | null = null;

export function getVariantCombinationManager(
  createStyleFn: (params: CreateStyleParams) => Style
): VariantCombinationManager {
  if (!combinationManagerInstance) {
    combinationManagerInstance = new VariantCombinationManager(createStyleFn);
  }
  return combinationManagerInstance;
}
